/**
 * ORCHID New York — the prototype's behaviour, ported to the theme.
 *
 * Deliberately dependency-free and tiny. Two jobs for now:
 *   1. the scroll reveal that staggers editorial blocks in
 *   2. the Brooklyn ZIP check
 *
 * The binding ZIP rule lives in checkout (a Brooklyn-only delivery area — Functions need Plus,
 * which this store is not on; see docs/CLIENT.md). This is only the courtesy answer before anyone
 * reaches checkout, and it must never be the thing standing between a customer and a sale.
 *
 * No image is ever expressed as a data: URI here. Content filters rewrite images in flight,
 * including ones they find inside JavaScript, which breaks the surrounding string and takes the
 * whole file down with it — invisible on an unfiltered machine.
 *
 * Every init function here must be safe to run again. The theme editor re-renders sections and
 * fires `shopify:section:load` without a page load, and this file once loaded twice (header and
 * hero both included it) — the Menu toggle bound twice opened and closed the panel in one click.
 * Every element that gets a listener is remembered in a WeakSet and skipped on the next pass.
 * A WeakSet, not a data attribute: an attribute survives innerHTML serialisation and would make
 * a freshly rendered copy look already bound. Fresh nodes are never in the set, so the editor
 * still works.
 */
(function () {
  'use strict';

  // If this file is ever included twice again, the second copy must not register a second set of
  // listeners. The first copy's `shopify:section:load` handler already re-runs init for it.
  if (window.orchidLoaded) return;
  window.orchidLoaded = true;

  var bound = new WeakSet();

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* --- Scroll reveal ------------------------------------------------ */

  // When the page last jumped rather than scrolled. Any reveal that fires
  // inside this window lands without its transition (see .is-instant): the
  // reader did not travel past the element, so it has nothing to arrive from.
  var jumpedAt = window.location.hash ? Date.now() : 0;
  function markJump() { jumpedAt = Date.now(); }
  window.addEventListener('hashchange', markJump);
  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('a[href^="#"]');
    if (link && link.getAttribute('href').length > 1) markJump();
  }, true);

  function initReveal() {
    if (reduced || !('IntersectionObserver' in window)) return;

    var targets = Array.prototype.filter.call(
      document.querySelectorAll('[data-orchid-reveal] > *'),
      function (el) { return !bound.has(el); }
    );
    if (!targets.length) return;

    targets.forEach(function (el) {
      bound.add(el);
      el.classList.add('orchid-reveal');
    });

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var siblings = Array.prototype.slice.call(entry.target.parentNode.children);
          var index = siblings.indexOf(entry.target);
          var jumping = Date.now() - jumpedAt < 1200;
          entry.target.style.setProperty('--orchid-reveal-delay', jumping ? '0ms' : index * 80 + 'ms');
          if (jumping) entry.target.classList.add('is-instant');
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
          if (jumping) {
            // Two frames: one for the class to apply at zero duration, one before
            // the normal duration is restored for any later, real scroll.
            window.requestAnimationFrame(function () {
              window.requestAnimationFrame(function () {
                entry.target.classList.remove('is-instant');
              });
            });
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );

    targets.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* --- Hero headline, word by word ---------------------------------- */

  function splitWords() {
    if (reduced) return;
    var nodes = document.querySelectorAll('[data-orchid-split]');
    Array.prototype.forEach.call(nodes, function (node) {
      if (bound.has(node)) return;
      var words = node.textContent.split(/\s+/).filter(Boolean);
      if (!words.length) return;
      bound.add(node);
      node.textContent = '';
      words.forEach(function (word, i) {
        var outer = document.createElement('span');
        outer.className = 'orchid-word';
        var inner = document.createElement('span');
        inner.textContent = word;
        // 40ms stagger, capped at 8 words: past that the last word of a headline
        // arrives so late it reads as a loading bug rather than as choreography.
        inner.style.setProperty('--orchid-word-delay', 80 + Math.min(i, 8) * 40 + 'ms');
        outer.appendChild(inner);
        node.appendChild(outer);
        if (i < words.length - 1) node.appendChild(document.createTextNode(' '));
      });
    });
  }

  /* --- Brooklyn ZIP check ------------------------------------------- */

  // Boro Park and the streets around it get the stated window; the rest of Brooklyn is on the
  // route but unscheduled; anything else gets a plain statement of where we deliver. The form
  // collects no contact details, so it must not promise to write — nobody could keep that.
  var CORE = ['11219', '11204', '11218', '11230', '11232'];

  function checkZip(value) {
    var zip = (value || '').replace(/\D/g, '');
    if (zip.length !== 5) {
      return { status: 'invalid', message: 'Five digits — your delivery ZIP.' };
    }
    if (CORE.indexOf(zip) !== -1) {
      return { status: 'eligible', message: 'On the route — Friday, 9:00 AM to 1:00 PM.' };
    }
    if (zip.indexOf('112') === 0) {
      return { status: 'eligible', message: 'On the Brooklyn route — Friday morning.' };
    }
    return {
      status: 'outside',
      message: 'We deliver across Brooklyn only for now.'
    };
  }

  function initZip() {
    var forms = document.querySelectorAll('[data-orchid-zip]');
    Array.prototype.forEach.call(forms, function (form) {
      if (bound.has(form)) return;
      var input = form.querySelector('input');
      var result = form.parentNode.querySelector('.orchid-zip__result');
      if (!input || !result) return;
      bound.add(form);

      // The idle line as authored, so the field can be put back to it.
      var idle = result.textContent;

      function render() {
        var outcome = checkZip(input.value);
        result.textContent = outcome.message;
        result.classList.toggle('orchid-zip__result--eligible', outcome.status === 'eligible');
      }

      // Only the button answers. It used to check on the fifth digit, which told
      // people "we deliver across Brooklyn only for now" while they were still
      // typing a ZIP that turned out to be fine — a rejection delivered mid-word,
      // then withdrawn. A ZIP is one thought; it gets one answer, when asked.
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        render();
      });

      // Typing only cleans the field and clears a stale answer, so a previous
      // verdict can never sit underneath a different number.
      input.addEventListener('input', function () {
        input.value = input.value.replace(/\D/g, '');
        if (result.textContent !== idle) {
          result.textContent = idle;
          result.classList.remove('orchid-zip__result--eligible');
        }
      });
    });
  }


  /* --- Tabletop add-ons ---------------------------------------------- */

  // A tap adds one, straight to the cart, and the page hears about it. The
  // buttons rendered with data-orchid-addon and a HAND-OFF comment for a year
  // and no script ever picked them up, so a customer tapped Add and got
  // silence. The variant comes from Liquid (a handle lookup in the section),
  // which is why a button with no variant is already disabled before we get
  // here — we only ever act on a live one.

  // Prestige's drawer listens for cart:change and then reads cart.sections[its
  // id]; asking for its section first is what keeps it from dereferencing
  // undefined. Same dance as orchid-cart.js and the chooser.
  function bundledSections() {
    var ask = { sections: [] };
    try {
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: ask })
      );
    } catch (error) {
      /* No drawer on this theme: nothing to bundle. */
    }
    return ask.sections.filter(function (id, i) {
      return id && ask.sections.indexOf(id) === i;
    });
  }

  function initAddons() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var buttons = document.querySelectorAll('[data-orchid-addon]');
    Array.prototype.forEach.call(buttons, function (button) {
      if (bound.has(button)) return;
      bound.add(button);

      button.addEventListener('click', function () {
        var variant = button.getAttribute('data-variant');
        if (!variant || button.disabled || button.classList.contains('is-busy')) return;

        var restingLabel = button.textContent;
        var addedLabel = button.getAttribute('data-added-label') || 'Added';
        button.classList.add('is-busy');
        button.setAttribute('aria-busy', 'true');

        var body = { items: [{ id: variant, quantity: 1 }] };
        var bundled = bundledSections();
        if (bundled.length) {
          body.sections = bundled.join(',');
          body.sections_url = window.location.pathname;
        }

        function settle(label, stateClass) {
          button.classList.remove('is-busy');
          button.removeAttribute('aria-busy');
          button.textContent = label;
          if (stateClass) button.classList.add(stateClass);
          window.setTimeout(function () {
            if (stateClass) button.classList.remove(stateClass);
            button.textContent = restingLabel;
          }, 2200);
        }

        fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        })
          .then(function (response) {
            return response.json().catch(function () { return {}; }).then(function (data) {
              if (!response.ok) throw new Error((data && data.description) || '');
              return data;
            });
          })
          .then(function (added) {
            // /cart/add.js answers with the lines it added, not the cart, so the
            // real cart is read for its totals and the rendered sections ride along.
            return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
              .then(function (response) { return response.json(); })
              .then(function (cart) {
                if (added && added.sections) cart.sections = added.sections;
                document.documentElement.dispatchEvent(
                  new CustomEvent('cart:change', {
                    bubbles: true,
                    detail: { baseEvent: 'orchid-addon:change', cart: cart }
                  })
                );
              })
              .catch(function () { /* the add itself succeeded */ });
          })
          .then(function () {
            settle(addedLabel, 'is-added');
          })
          .catch(function () {
            settle('Try again', 'is-error');
          });
      });
    });
  }


  /* --- Header: shrink past the threshold, stay pinned ---------------- */

  // One scroll listener at a time. When the editor re-renders the header the old element is
  // gone; the listener that pointed at it goes with it, so we never toggle a detached node.
  var onScroll = null;

  function cartLabel(count) {
    var n = parseInt(count, 10);
    if (isNaN(n)) n = 0;
    return 'Cart, ' + n + (n === 1 ? ' item' : ' items');
  }

  function initHeader() {
    var header = document.querySelector('orchid-header');
    if (!header || bound.has(header)) return;
    bound.add(header);

    var threshold = parseInt(header.getAttribute('data-compact-at'), 10) || 120;
    var ticking = false;

    // Direction-aware, not just position-aware. Compacting on depth alone means
    // the nav only comes back when the reader has scrolled all the way to the
    // top again, which is a long way up from the footer for a link they can see
    // they need now. Scrolling UP is the reader asking for navigation, so the
    // header answers immediately, wherever they are on the page.
    //
    // The whole difficulty is that this control MOVES THE PAGE IT MEASURES.
    // Expanding grows the bar by ~36px, the content below shifts, scrollY
    // changes, and that change reads as a fresh scroll in the opposite
    // direction — which flips the state back, which shifts the page back. It
    // oscillates, and near the threshold it visibly blinks. Three things stop
    // it, and all three are needed:
    //
    //   1. Separate thresholds. Compact on the way past COMPACT_AT, expand on
    //      the way back above EXPAND_AT, with a gap between them, so crossing
    //      one boundary cannot immediately satisfy the other.
    //   2. Accumulated travel, not per-frame delta. The reader has to actually
    //      move INTENT_PX in one direction; a reflow of a few pixels never adds
    //      up to it, and a slow deliberate drag always does.
    //   3. A cooldown as long as the CSS transition. While the bar is still
    //      animating, every frame is reporting scroll positions caused by the
    //      animation itself, so no state change is allowed to happen from them.
    var COMPACT_AT = threshold;            // going down: compact past here
    var EXPAND_AT = Math.max(0, threshold - 60);  // coming up: expanded above here
    var INTENT_PX = 28;                    // deliberate travel before the state flips
    var SETTLE_MS = 560;                   // the bar's own 500ms transition, plus air

    var lastY = window.scrollY;
    var upFrom = lastY;      // where the current run of upward travel began
    var downFrom = lastY;    // and the current run of downward travel
    var lockUntil = 0;

    function setCompact(on) {
      if (header.classList.contains('is-compact') === on) return;
      // Landing from a jump (see jumpedAt, above initReveal): switch without the
      // 500ms glide. The page has just cut to a new place; a header seen
      // shrinking over half a second at that moment reads as the page blinking.
      var jumping = Date.now() - jumpedAt < 1200;
      if (jumping) header.classList.add('is-instant');
      header.classList.toggle('is-compact', on);
      lockUntil = Date.now() + (jumping ? 0 : SETTLE_MS);
      if (jumping) {
        window.requestAnimationFrame(function () {
          window.requestAnimationFrame(function () {
            header.classList.remove('is-instant');
          });
        });
      }
    }

    function apply() {
      ticking = false;
      var y = window.scrollY;

      if (y > lastY) { upFrom = y; }        // moving down: reset the up run
      else if (y < lastY) { downFrom = y; } // moving up: reset the down run
      lastY = y;

      // Above the top boundary the header is always whole, and that is not a
      // state change worth locking: it is where the page starts.
      if (y <= EXPAND_AT) {
        header.classList.remove('is-compact');
        upFrom = downFrom = y;
        return;
      }

      if (Date.now() < lockUntil) return;

      if (upFrom - y >= INTENT_PX) {
        setCompact(false);
        upFrom = downFrom = y;
      } else if (y - downFrom >= INTENT_PX && y > COMPACT_AT) {
        setCompact(true);
        upFrom = downFrom = y;
      }
    }

    apply();
    if (onScroll) window.removeEventListener('scroll', onScroll);
    onScroll = function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(apply);
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    // Cart link: "Cart, N items" as the accessible name, kept in step with Prestige's
    // <cart-count>, which rewrites its own text on cart:change, cart:refresh and pageshow.
    // Watching the element catches every one of those paths without duplicating its logic.
    var cart = header.querySelector('[data-orchid-header-cart]');
    var count = cart && cart.querySelector('cart-count');
    if (cart && count) {
      var syncCart = function () {
        var n = parseInt(count.textContent.trim(), 10);
        cart.setAttribute('aria-label', cartLabel(count.textContent.trim()));
        // Filled badge when there is something in it, quiet outline when not.
        cart.classList.toggle('is-empty', !(n > 0));
      };
      syncCart();
      if ('MutationObserver' in window) {
        new MutationObserver(syncCart).observe(count, {
          childList: true,
          characterData: true,
          subtree: true
        });
      } else {
        document.addEventListener('cart:change', function () {
          window.setTimeout(syncCart, 0);
        });
      }
    }

    var toggle = header.querySelector('[data-orchid-menu]');
    var panel = header.querySelector('#orchid-mobile-nav');
    var label = header.querySelector('[data-orchid-menu-label]');
    if (!toggle || !panel) return;

    toggle.addEventListener('click', function () {
      var open = panel.hasAttribute('hidden');
      if (open) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      // A clean ✕ when open rather than the word CLOSE; the accessible name still
      // says what it does. The page behind the drawer stays put.
      if (label) {
        label.textContent = open ? '\u2715' : 'Menu';
        label.classList.toggle('orchid-header__menu-close', open);
      }
      toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      document.documentElement.classList.toggle('orchid-nav-open', open);
    });
  }


  /* --- The Friday chip links into the rotation ----------------------- */

  // Two things have to happen when the chip is tapped, and no single mechanism gives both.
  // Prestige delegates every same-page `a[href*="#"]` click to its own scroller: it scrolls
  // nicely but deliberately never sets location.hash, so :target - which is what lights the week
  // up - never matches. Going around it with [allow-hash-change] sets the hash but the browser's
  // own fragment scroll does not fire reliably on this page. So do both here, in the open.
  //
  // This binds on the link itself, so it runs before Prestige's delegate on documentElement;
  // preventDefault then makes that handler bail on its own `event.defaultPrevented` check. If
  // this file ever fails to load, Prestige still scrolls - the visitor gets there, just without
  // the highlight.
  function initChipLink() {
    var links = document.querySelectorAll('[data-orchid-chip-link]');
    Array.prototype.forEach.call(links, function (link) {
      if (bound.has(link)) return;
      bound.add(link);

      link.addEventListener('click', function (event) {
        var hash = link.getAttribute('href') || '';
        if (hash.charAt(0) !== '#') return;

        var target;
        try {
          target = document.querySelector(hash);
        } catch (e) {
          return;
        }
        // No rotation section on this page: leave the link alone rather than swallow the click.
        if (!target) return;

        event.preventDefault();

        // Set the fragment first - that is what :target reads - then scroll ourselves. Assigning
        // the same value again is a no-op, which is why the scroll is a separate call: tapping
        // the chip a second time still takes you back to the week.
        if (window.location.hash !== hash) window.location.hash = hash;

        // 'instant', and both halves of that matter.
        //
        // The theme sets `scroll-behavior: smooth` on <html>, and in the CSSOM 'auto' means "use
        // the element's computed scroll-behavior" - so 'auto' here would still animate. 'instant'
        // is the only value that overrides the stylesheet.
        //
        // And it has to be overridden: the rotation sits ~2000px below the hero, and an animated
        // scroll across that distance does not survive the lazy images loading on the way. On the
        // live page it consistently stalled around 44px and left the visitor in the hero, looking
        // at a tap that did nothing. An instant landing is no loss anyway - the week lights up on
        // arrival, and that is what tells you the tap worked.
        target.scrollIntoView({ block: 'start', behavior: 'instant' });
      });
    });
  }

  function init() {
    initHeader();
    initReveal();
    splitWords();
    initZip();
    initAddons();
    initChipLink();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', init);
})();
